/**
 * Global "no image" fallback.
 *
 * Images that used to live in S3 buckets which no longer exist have had their
 * URLs cleared, so those records now carry an empty src.  Rather than editing
 * every <img> in the app, one capture-phase listener swaps any image that
 * fails to load for a neutral placeholder.
 *
 * Capture phase is required: the error event on an <img> does not bubble, so a
 * normal listener on document would never fire.
 */

/** Inline SVG, so the placeholder itself can never fail to load. */
export const PLACEHOLDER_IMAGE =
  "data:image/svg+xml;charset=UTF-8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 150" preserveAspectRatio="xMidYMid meet">
       <rect width="200" height="150" fill="#f1f3f5"/>
       <rect x="0.5" y="0.5" width="199" height="149" fill="none" stroke="#dee2e6"/>
       <g fill="#adb5bd">
         <circle cx="72" cy="56" r="9"/>
         <path d="M42 104 L78 68 L106 96 L128 82 L158 108 Z"/>
       </g>
       <text x="100" y="133" text-anchor="middle" fill="#868e96"
             font-family="system-ui, -apple-system, Segoe UI, sans-serif" font-size="13">No image</text>
     </svg>`
  );

const APPLIED = "imgFallbackApplied";

/** Registers the listener once. Safe to call more than once. */
export const installImageFallback = (): void => {
  if ((window as any).__imageFallbackInstalled) return;
  (window as any).__imageFallbackInstalled = true;

  document.addEventListener(
    "error",
    (event: Event) => {
      const target = event.target as HTMLElement | null;
      if (!target || target.tagName !== "IMG") return;

      const img = target as HTMLImageElement;

      // Guard against a loop if the placeholder itself ever fails.
      if (img.dataset[APPLIED] === "true") return;
      img.dataset[APPLIED] = "true";

      img.src = PLACEHOLDER_IMAGE;
    },
    true // capture - image error events do not bubble
  );
};

/**
 * For places that would rather resolve the src up front than wait for a failed
 * request, e.g. a background-image, which the listener above cannot catch.
 */
export const imageSrcOrPlaceholder = (url?: string | null): string =>
  url && url.trim() ? url : PLACEHOLDER_IMAGE;
